import { useState, createContext, useContext } from "react";
import { Link, useLocation } from "react-router-dom"; 
import {
  LayoutDashboard,
  Users,
  TimerReset,
  FileText,
  Settings,
  ShieldCheck,
  ChevronLeft,
  Camera,
  ChevronRight,
  IdCardLanyard ,
  LogOut,
  Clock,
  Sparkles,
  House,
  Menu,
  X
} from "lucide-react";

const SidebarContext = createContext();

export default function SideBar() {
  const [expanded, setExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Get current location to highlight the active menu item
  const location = useLocation(); 

  // ✅ FIX: Changed Settings path and gave Pending Bills a proper icon
  const menu = [
    { label: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
    { label: "Home", path: "/", icon: House },
    { label: "Attendance", path: "/admin/Attendance", icon: Users }, 
    { label: "Bills", path: "/admin/Bills", icon: FileText, alert: true },
    { label: "Pending Bills", path: "/admin/Pending-Bills", icon: Clock }, // Uses Clock icon
    { label: "Customer Dues", path: "/admin/Pending-Bills-List", icon: TimerReset },
    { label: "ID Cards", path: "/admin/ID-Cards-Generator-DailyGo", icon: IdCardLanyard },
    { label: "Identity Report", path: "/admin/Identity-Report", icon: Camera },
    { label: "Settings", path: "/admin/Settings", icon: Settings }, 
  ];

  return (
    <>
      {/* Mobile Trigger Button */}
      <button 
        onClick={() => setMobileOpen(true)}
        className={`fixed top-4 left-4 z-50 p-2 bg-slate-900/80 text-white rounded-lg backdrop-blur-md border border-slate-700 md:hidden ${mobileOpen ? "hidden" : "block"}`}
      >
        <Menu size={24} />
      </button>

      {/* Mobile Backdrop */}
      {mobileOpen && (
        <div 
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden transition-opacity"
        />
      )}

      {/* Sidebar Container */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50
          md:sticky md:top-0 md:h-screen
          bg-slate-900/60 backdrop-blur-xl border-r border-slate-700/40
          shadow-[4px_0_24px_-2px_rgba(0,0,0,0.5)]
          transition-all duration-300 ease-in-out
          flex flex-col
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          ${expanded ? "md:w-72" : "md:w-20"}
          w-72
        `}
      >
        <nav className="h-full flex flex-col justify-between">
          
          {/* Top Section */}
          <div>
            {/* Header & Logo */}
            <div className="p-4 pb-2 flex justify-between items-center relative">
              <div className={`flex items-center gap-3 transition-all duration-300 ${expanded ? "justify-start" : "justify-center w-full"}`}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-indigo-500 rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
                  <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-800 flex items-center justify-center text-white ring-1 ring-white/20 shadow-lg">
                    <ShieldCheck size={24} />
                  </div>
                </div>
                <div className={`flex flex-col overflow-hidden transition-all duration-300 ${expanded ? "w-40 ml-0 opacity-100" : "md:w-0 md:ml-0 md:opacity-0"}`}>
                  <h1 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400 leading-tight tracking-wide">
                    Admin Panel
                  </h1>
                  <span className="text-[10px] text-indigo-400 font-semibold uppercase tracking-widest flex items-center gap-1">
                    DailyGoLive <Sparkles size={8} />
                  </span>
                </div>
              </div>

              {/* Desktop Toggle */}
              <button 
                onClick={() => setExpanded((curr) => !curr)} 
                className="hidden md:block absolute -right-3 top-7 bg-slate-800 text-slate-400 border border-slate-600 rounded-full p-1.5 shadow-lg hover:text-white hover:bg-indigo-600 transition-all transform hover:scale-110"
              >
                {expanded ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
              </button>

              {/* Mobile Close */}
              <button 
                onClick={() => setMobileOpen(false)} 
                className="md:hidden p-1.5 text-slate-400 hover:text-white bg-slate-800/50 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Menu Items */}
            <SidebarContext.Provider value={{ expanded }}>
              <ul className="px-3 py-6 space-y-2">
                {menu.map((item) => (
                  <SidebarItem 
                    key={item.path} 
                    {...item} 
                    active={location.pathname === item.path}
                  />
                ))}
              </ul>
            </SidebarContext.Provider>
          </div>

          {/* Footer / User Profile */}
          <div className="p-3 border-t border-slate-700/30 bg-slate-900/20 mt-auto">
            <div className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer hover:bg-slate-800/50 group ${expanded ? "" : "justify-center"}`}>
              <div className="relative">
                <img src="https://ui-avatars.com/api/?name=Hanish+Dev&background=3730a3&color=fff" className="w-9 h-9 rounded-lg shadow-md" alt="User" />
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-900"></div>
              </div>
              <div className={`flex justify-between items-center overflow-hidden transition-all duration-300 ${expanded ? "w-40 ml-0 opacity-100" : "md:w-0 md:opacity-0"}`}>
                <div className="leading-4">
                  <h4 className="font-semibold text-sm text-slate-200 group-hover:text-white">Hanish</h4>
                  <span className="text-xs text-slate-500 group-hover:text-indigo-400">dev@fyntraxis.in</span>
                </div>
                <LogOut size={18} className="text-slate-500 hover:text-red-400 transition-colors" />
              </div>
            </div>
          </div>
        </nav>
      </aside>
    </>
  );
}

// --- Sidebar Item Component ---
function SidebarItem({ icon: Icon, label, path, active, alert }) {
  const { expanded } = useContext(SidebarContext);

  return (
    <li className="relative group">
      <Link
        to={path}
        className={`
          relative flex items-center py-3 px-3.5 my-1
          font-medium rounded-xl cursor-pointer
          transition-all duration-300 ease-out group
          ${active 
            ? "bg-gradient-to-r from-indigo-600/90 to-indigo-800/90 text-white shadow-[0_0_20px_rgba(79,70,229,0.4)] border border-indigo-500/50" 
            : "text-slate-400 hover:bg-slate-800/40 hover:text-indigo-200 hover:border-slate-700 border border-transparent"
          }
        `}
      >
        <Icon size={20} className={`transition-all duration-300 ${!expanded && "md:mx-auto"} ${active ? "drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" : ""}`} />
        
        <span className={`overflow-hidden transition-all duration-300 whitespace-nowrap ${expanded ? "w-40 ml-3 opacity-100" : "md:w-0 md:opacity-0 w-40 ml-3 opacity-100"}`}>
          {label}
        </span>

        {active && <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/10 to-transparent pointer-events-none" />}

        {alert && (
          <div className={`absolute right-3 w-2 h-2 rounded-full bg-indigo-400 shadow-[0_0_8px_rgba(129,140,248,0.6)] ${expanded ? "" : "md:top-2 md:right-2"}`} />
        )}

        {/* Hover Tooltip (Desktop only, when collapsed) */}
        {!expanded && (
          <div className={`
            hidden md:block
            absolute left-full rounded-md px-3 py-1.5 ml-6
            bg-slate-800/90 backdrop-blur-md border border-slate-700
            text-indigo-100 text-sm font-medium
            invisible opacity-0 -translate-x-3 transition-all duration-200
            group-hover:visible group-hover:opacity-100 group-hover:translate-x-0
            whitespace-nowrap z-50
          `}>
            {label}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 border-4 border-transparent border-r-slate-800/90"></div>
          </div>
        )}
      </Link>
    </li>
  );
}